// ==========================================
// FILE: \src\screens\StatsScreen.js
// ==========================================

import React, { useState, useEffect, useMemo } from 'react';
import { 
  View, Text, StyleSheet, TouchableOpacity, ScrollView, 
  ActivityIndicator, Platform, Modal
} from 'react-native';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { useWineContext } from '../context/WineContext'; 
import { callGemini } from '../utils/geminiClient'; 
import EnergyHeader from '../components/EnergyHeader'; 
import { 
  ArrowLeft, PieChart, Sparkles, 
  Wine, TrendingUp, AlertCircle, CheckCircle2, ChevronRight
} from 'lucide-react-native';

import OutOfEnergyModal from '../components/OutOfEnergyModal';
import RewardedAdModal from '../components/RewardedAdModal';

export default function StatsScreen({ navigation }) {
  const { presentWines, isLoadingWines, consumeEnergy, addEnergy, userProfile, t } = useWineContext();
  const isPremium = userProfile?.isPremium || false;
  const user = auth.currentUser;

  const [auditData, setAuditData] = useState(null);
  const [isLoadingAudit, setIsLoadingAudit] = useState(true);
  const [isGeneratingAudit, setIsGeneratingAudit] = useState(false);
  
  const [dailySuggestions, setDailySuggestions] = useState(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);
  const [isPickingWine, setIsPickingWine] = useState(false);

  const [customAlert, setCustomAlert] = useState({ visible: false, title: '', message: '', type: 'info' });

  const [showOutOfEnergy, setShowOutOfEnergy] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);

  const totalBottles = presentWines.reduce((acc, wine) => acc + (wine.quantity || 0), 0);
  const totalValue = presentWines.reduce((acc, wine) => acc + ((wine.estimatedValue || 0) * wine.quantity), 0);
  
  const readyToDrinkCount = presentWines.filter(w => w.isReadyToDrink).reduce((acc, w) => acc + w.quantity, 0);
  const agingCount = totalBottles - readyToDrinkCount;

  const todayStr = new Date().toLocaleDateString('en-CA'); 

  const showAppAlert = (title, message, type = 'info') => setCustomAlert({ visible: true, title, message, type });
  const closeAppAlert = () => setCustomAlert({ visible: false, title: '', message: '', type: 'info' });

  const handleAdComplete = () => {
    setShowAdModal(false);
    addEnergy(5);
    showAppAlert(t('home.alerts.adSuccessTitle'), t('home.alerts.adSuccessMsg'), "success");
  };

  // Verifica se l'Audit memorizzato necessita di un riallineamento linguistico (AI translation check)
  const auditNeedsTranslation = useMemo(() => {
    if (!auditData) return false;
    const currentAppLang = userProfile?.language || 'it';
    const currentDataLang = auditData.generatedLanguage || 'it';
    return currentDataLang !== currentAppLang;
  }, [auditData, userProfile?.language]);

  const isAuditOutdated = auditData && (auditData.lastBottleCount !== totalBottles || auditNeedsTranslation);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        const auditRef = doc(db, 'users', user.uid, 'stats', 'audit');
        const auditSnap = await getDoc(auditRef);
        if (auditSnap.exists()) setAuditData(auditSnap.data());

        const suggestionRef = doc(db, 'users', user.uid, 'stats', 'dailySuggestion');
        const suggestionSnap = await getDoc(suggestionRef);
        
        if (suggestionSnap.exists()) {
          const data = suggestionSnap.data();
          const currentAppLang = userProfile?.language || 'it';
          const currentDataLang = data.generatedLanguage || 'it';
          
          // Se la data corrisponde ed è allineata alla lingua corrente dell'utente, carichiamo la cache
          if (data.date === todayStr && currentDataLang === currentAppLang) {
            setDailySuggestions(data.wines);
          } else {
            setDailySuggestions(null); // Forza l'azzeramento se è cambiata la lingua
          }
        }
      } catch (error) {
        console.log("Errore caricamento statistiche:", error);
      } finally {
        setIsLoadingAudit(false);
        setIsLoadingSuggestions(false);
      }
    };
    fetchData();
  }, [user, userProfile?.language]);

  const getWineSuggestions = async () => {
    if (isPickingWine) return; 
    if (presentWines.length === 0) return showAppAlert(t('stats.suggestions.emptyTitle'), t('stats.suggestions.emptyMsg'), "info");
    if (!user) return;
    
    setIsPickingWine(true); 

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsPickingWine(false); 
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsPickingWine(false); 
      return;
    }

    try {
      const cellarSummary = presentWines.map(w => ({
        id: w.id, name: w.name, annata: w.annata, drinkingWindow: w.drinkingWindow, isReady: w.isReadyToDrink
      }));

      const alreadySuggestedIds = dailySuggestions ? dailySuggestions.map(s => s.id) : [];

      const langNames = { it: "Italiano", en: "Inglese (English)", fr: "Francese (Français)" };
      const targetLanguageName = langNames[userProfile?.language || 'it'] || "Italiano";

      const promptText = `Sei un Master Sommelier. L'utente ti chiede: "Quale vino mi consigli di stapparsi in questo periodo?".
      Questa è la sua cantina in formato JSON: ${JSON.stringify(cellarSummary)}
      
      REGOLE ASSOLUTE: 
      1. LINGUA: Tutti i testi descrittivi generati ("reason") DEVONO essere scritti rigorosamente in lingua ${targetLanguageName}.
      2. Seleziona 3 bottiglie da stappare ORA, in ordine di priorità decrescente.
      3. Dai priorità ai vini che sono nella loro "forchetta di consumo" o che rischiano di superare il limite.
      4. NON SUGGERIRE MAI questi ID, perché li hai già consigliati oggi: ${JSON.stringify(alreadySuggestedIds)}. Trova altre 3 alternative.
      5. Motiva la scelta in 2-3 righe in modo affascinante in lingua ${targetLanguageName}.
      
      Rispondi SOLO con questo JSON esatto (un array di 3 oggetti):
      [
        { "id": "id_vino_1", "reason": "Motivazione in lingua ${targetLanguageName}" },
        { "id": "id_vino_2", "reason": "Motivazione in lingua ${targetLanguageName}" },
        { "id": "id_vino_3", "reason": "Motivazione in lingua ${targetLanguageName}" }
      ]`;

      const extractedData = await callGemini(promptText, true);
      const newSuggestionsList = dailySuggestions ? [...dailySuggestions, ...extractedData] : extractedData;

      const newSuggestionData = {
        date: todayStr,
        wines: newSuggestionsList,
        generatedLanguage: userProfile?.language || 'it' // Tracciamo legalmente la lingua di generazione
      };
      
      await setDoc(doc(db, 'users', user.uid, 'stats', 'dailySuggestion'), newSuggestionData);
      setDailySuggestions(newSuggestionsList);

    } catch (error) {
      if (!isPremium) await addEnergy(1);
      showAppAlert(t('map.alerts.geoErrorTitle'), t('map.alerts.geoErrorMsg'), "error");
    } finally {
      setIsPickingWine(false); 
    }
  };

  const generateAudit = async () => {
    if (isGeneratingAudit) return; 
    if (presentWines.length === 0) return showAppAlert(t('stats.suggestions.emptyTitle'), t('stats.audit.emptyText'), "info");
    if (!user) return;

    setIsGeneratingAudit(true); 

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsGeneratingAudit(false); 
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsGeneratingAudit(false); 
      return;
    }

    try {
      const cellarSummary = presentWines.map(w => ({
        name: w.name, type: w.type, vitigno: w.technicalSheet?.vitigno, regione: w.technicalSheet?.regione, qty: w.quantity
      }));

      const langNames = { it: "Italiano", en: "Inglese (English)", fr: "Francese (Français)" };
      const targetLanguageName = langNames[userProfile?.language || 'it'] || "Italiano";

      const promptText = `Sei un Master Sommelier che sta analizzando il Caveau di un collezionista.
      Cantina: ${JSON.stringify(cellarSummary)}
      
      COMPITI:
      1. Raggruppa i vini in massimo 5 o 6 categorie enologiche AVANZATE (es. "Grandi Rossi da Invecchiamento", "Bianchi Aromatici", "Bollicine Metodo Classico"). Tutti i nomi delle categorie ("name") e il resoconto testuale ("report") DEVONO essere scritti in lingua ${targetLanguageName}.
      2. Scrivi un report testuale (2 paragrafi) valutando l'equilibrio della cantina e suggerendo cosa manca, rigorosamente in lingua ${targetLanguageName}.

      Rispondi SOLO con questo JSON esatto:
      {
        "categories": [
          {"name": "Nome Categoria in lingua ${targetLanguageName}", "count": 12}
        ],
        "report": "Testo del report in lingua ${targetLanguageName}..."
      }`;

      const extractedData = await callGemini(promptText, true);

      const newAuditData = {
        categories: extractedData.categories,
        report: extractedData.report,
        lastBottleCount: totalBottles, 
        updatedAt: Date.now(),
        generatedLanguage: userProfile?.language || 'it' // Flag per tracciare la lingua di genesi nel DB
      };

      await setDoc(doc(db, 'users', user.uid, 'stats', 'audit'), newAuditData);
      setAuditData(newAuditData);
      showAppAlert(t('stats.audit.successTitle'), t('stats.audit.successMsg'), "success");

    } catch (error) {
      if (!isPremium) await addEnergy(1);
      showAppAlert(t('map.alerts.geoErrorTitle'), t('map.alerts.terroirErrorMsg'), "error");
    } finally {
      setIsGeneratingAudit(false); 
    }
  };

  const renderProgressBar = (label, count, total, colorHex, isDarkTheme = false) => {
    const percentage = total > 0 ? (count / total) * 100 : 0;
    return (
      <View key={label} style={styles.barContainer}>
        <View style={styles.barLabelRow}>
          <Text style={[styles.barLabel, isDarkTheme ? { color: '#ffffff' } : { color: '#0f172a' }]} numberOfLines={1}>{label}</Text>
          <Text style={[styles.barValue, isDarkTheme ? { color: '#cbd5e1' } : { color: '#64748b' }]}>{count} ({percentage.toFixed(0)}%)</Text>
        </View>
        <View style={[styles.barBackground, isDarkTheme ? { backgroundColor: 'rgba(255,255,255,0.15)' } : { backgroundColor: '#f1f5f9' }]}>
          <View style={[styles.barFill, { width: `${percentage}%`, backgroundColor: colorHex }]} />
        </View>
      </View>
    );
  };

  return (
    <View style={styles.mainContainer}>
      
      <View style={styles.topRedBanner}>
         <EnergyHeader />
      </View>

      <View style={styles.pageHeaderCushion}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <ArrowLeft color="#0f172a" size={24} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('stats.title')}</Text>
        <View style={{width: 36}} /> 
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        
        {/* SCHEDA PATRIMONIO ED INVENTARIO MATURAZIONE */}
        <View style={styles.heroCard}>
          <View style={styles.heroTop}>
            <TrendingUp color="#fef08a" size={24} />
            <Text style={styles.heroSubtitle}>{t('stats.hero.subtitle')}</Text>
          </View>
          <Text style={styles.heroTitle}>{totalValue.toLocaleString(userProfile?.language === 'it' ? 'it-IT' : 'en-US')} €</Text>
          <View style={styles.heroBottom}>
            <Wine color="#fecdd3" size={16} style={{marginRight: 6}} />
            <Text style={styles.heroBottomText}>{totalBottles} {t('stats.hero.bottles')}</Text>
          </View>

          <View style={styles.bevaContainer}>
            <Text style={styles.bevaTitle}>{t('stats.hero.maturation')}</Text>
            {renderProgressBar(t('stats.hero.ready'), readyToDrinkCount, totalBottles, '#4ade80', true)}
            {renderProgressBar(t('stats.hero.aging'), agingCount, totalBottles, '#facc15', true)}
          </View>
        </View>

        {/* SEZIONE COMPATIBILE CON I CONSIGLI QUOTIDIANI DEL SOMMELIER */}
        <View style={styles.aiSection}>
          <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 6}}>
            <Sparkles color="#4f46e5" size={22} style={{marginRight: 4}} />
            <Text style={styles.aiSectionTitle}>{t('stats.suggestions.title')}</Text>
          </View>
          
          <Text style={styles.aiSectionSub}>{t('stats.suggestions.sub')}</Text>

          {isLoadingSuggestions ? (
             <ActivityIndicator color="#4f46e5" style={{marginVertical: 20}} />
          ) : !dailySuggestions || dailySuggestions.length === 0 ? (
            <TouchableOpacity style={styles.aiButton} onPress={getWineSuggestions} disabled={isPickingWine}>
              {isPickingWine ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.aiButtonText}>{t('stats.suggestions.ask')}</Text>
              )}
            </TouchableOpacity>
          ) : (
            <View style={styles.suggestionsContainer}>
              {dailySuggestions.map((suggestion, index) => {
                const matchedWine = presentWines.find(w => w.id === suggestion.id);
                if (!matchedWine) return null; 

                return (
                  <View key={index} style={styles.suggestionCard}>
                    <View style={styles.suggestionRankBadge}>
                      <Text style={styles.suggestionRankText}>{t('stats.suggestions.option')} {index + 1}</Text>
                    </View>
                    <Text style={styles.suggestionWineName}>{matchedWine.name}</Text>
                    <Text style={styles.suggestionReason}>{suggestion.reason}</Text>
                    <TouchableOpacity 
                      style={styles.openWineBtn}
                      onPress={() => navigation.navigate('Detail', { wine: matchedWine })}
                    >
                      <Text style={styles.openWineBtnText}>{t('stats.suggestions.openSheet')}</Text>
                      <ChevronRight color="#4f46e5" size={16} />
                    </TouchableOpacity>
                  </View>
                );
              })}

              <TouchableOpacity style={styles.recalcSecondaryBtn} onPress={getWineSuggestions} disabled={isPickingWine}>
                {isPickingWine ? (
                  <ActivityIndicator color="#4f46e5" />
                ) : (
                  <Text style={styles.recalcSecondaryBtnText}>{t('stats.suggestions.more')}</Text>
                )}
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* SEZIONE AUDIT COMPOSIZIONE CANTINA */}
        <View style={styles.sectionHeaderWrap}>
          <PieChart color="#0f172a" size={22} />
          <Text style={styles.sectionLabel}>{t('stats.audit.title')}</Text>
        </View>

        <View style={[styles.card, isAuditOutdated && styles.cardOutdated]}>
          {isLoadingAudit ? (
            <ActivityIndicator color="#e11d48" style={{marginVertical: 20}} />
          ) : auditData ? (
            <>
              {isAuditOutdated && (
                <View style={styles.outdatedBanner}>
                  <AlertCircle color="#ea580c" size={18} />
                  <Text style={styles.outdatedText}>
                    {auditNeedsTranslation ? t('stats.audit.translate') : t('stats.audit.outdated')}
                  </Text>
                </View>
              )}

              <View style={[isAuditOutdated && {opacity: 0.5}]}>
                {auditData.categories?.map((cat, idx) => {
                  const colors = ['#be123c', '#4f46e5', '#d97706', '#0ea5e9', '#16a34a', '#9333ea'];
                  return renderProgressBar(cat.name, cat.count, auditData.lastBottleCount, colors[idx % colors.length], false);
                })}
              </View>

              <View style={[styles.reportBox, isAuditOutdated && {opacity: 0.5}]}>
                <Text style={styles.reportTitle}>{t('stats.audit.expert')}</Text>
                <Text style={styles.reportText}>{auditData.report}</Text>
              </View>

              {isAuditOutdated && (
                <TouchableOpacity style={styles.recalcBtn} onPress={generateAudit} disabled={isGeneratingAudit}>
                  {isGeneratingAudit ? <ActivityIndicator color="#fff" /> : <Text style={styles.recalcBtnText}>{t('stats.audit.recalc')}</Text>}
                </TouchableOpacity>
              )}
            </>
          ) : (
            <View style={styles.emptyAuditBox}>
              <Wine color="#94a3b8" size={40} style={{marginBottom: 12}} />
              <Text style={styles.emptyAuditTitle}>{t('stats.audit.emptyTitle')}</Text>
              <Text style={styles.emptyAuditText}>{t('stats.audit.emptyText')}</Text>
              <TouchableOpacity style={styles.recalcBtn} onPress={generateAudit} disabled={isGeneratingAudit}>
                {isGeneratingAudit ? <ActivityIndicator color="#fff" /> : <Text style={styles.recalcBtnText}>{t('stats.audit.generate')}</Text>}
              </TouchableOpacity>
            </View>
          )}
        </View>

      </ScrollView>

      {/* POPUP MODALE AVVISI DI SISTEMA */}
      <Modal transparent={true} visible={customAlert.visible} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBoxBase}>
            <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 16}}>
              {customAlert.type === 'error' ? <AlertCircle color="#ef4444" size={28} /> : <CheckCircle2 color="#10b981" size={28} />}
              <Text style={styles.modalTitle}>{customAlert.title}</Text>
            </View>
            <Text style={styles.modalMessage}>{customAlert.message}</Text>
            <TouchableOpacity style={styles.modalConfirmBtn} onPress={closeAppAlert}>
              <Text style={styles.modalConfirmText}>{t('global.ok')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <OutOfEnergyModal 
        visible={showOutOfEnergy}
        onClose={() => setShowOutOfEnergy(false)}
        onWatchAd={() => {
          setShowOutOfEnergy(false);
          setShowAdModal(true); 
        }}
        onSubscribe={() => {
          setShowOutOfEnergy(false);
          navigation.navigate('Paywall');
        }}
      />

      <RewardedAdModal 
        visible={showAdModal}
        onClose={() => setShowAdModal(false)}
        onReward={handleAdComplete}
      />

    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: { flex: 1, backgroundColor: '#f8fafc' },
  topRedBanner: {
    backgroundColor: '#e11d48',
    paddingBottom: 16,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    elevation: 5,
    boxShadow: '0px 4px 6px rgba(225, 29, 72, 0.3)', 
    zIndex: 50,
  },
  pageHeaderCushion: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 16, 
    marginBottom: 16,
    padding: 16,
    borderRadius: 20,
    elevation: 3,
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.05)', 
    zIndex: 10,
  },
  backButton: { padding: 6, backgroundColor: '#f1f5f9', borderRadius: 10 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a' },
  scrollContent: { paddingHorizontal: 16, paddingBottom: 60 },
  heroCard: { backgroundColor: '#9f1239', borderRadius: 24, padding: 24, marginBottom: 24, boxShadow: '0px 8px 20px rgba(159, 18, 57, 0.25)', elevation: 8 },
  heroTop: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  heroSubtitle: { color: '#fef08a', fontSize: 16, fontWeight: '600', marginLeft: 8, textTransform: 'uppercase' },
  heroTitle: { fontSize: 32, fontWeight: 'bold', color: 'white', marginBottom: 16 },
  heroBottom: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.1)', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, alignSelf: 'flex-start', marginBottom: 24 },
  heroBottomText: { color: '#fecdd3', fontWeight: 'bold', fontSize: 14 },
  bevaContainer: { backgroundColor: 'rgba(0,0,0,0.2)', padding: 16, borderRadius: 16 },
  bevaTitle: { color: 'white', fontWeight: 'bold', marginBottom: 12, fontSize: 15 },
  aiSection: { backgroundColor: '#e0e7ff', padding: 24, borderRadius: 24, marginBottom: 32, borderWidth: 1, borderColor: '#c7d2fe', boxShadow: '0px 4px 12px rgba(79, 70, 229, 0.15)', elevation: 5 },
  aiSectionTitle: { fontSize: 18, fontWeight: 'bold', color: '#312e81', flex: 1 },
  aiSectionSub: { fontSize: 14, color: '#4338ca', lineHeight: 20, marginBottom: 20 },
  aiButton: { backgroundColor: '#4f46e5', paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  aiButtonText: { color: 'white', fontWeight: 'bold', fontSize: 16 },
  suggestionsContainer: { gap: 12 },
  suggestionCard: { backgroundColor: 'white', borderRadius: 16, padding: 16, borderWidth: 1, borderColor: '#e2e8f0' },
  suggestionRankBadge: { alignSelf: 'flex-start', backgroundColor: '#f1f5f9', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, marginBottom: 8 },
  suggestionRankText: { color: '#64748b', fontWeight: 'bold', fontSize: 12, textTransform: 'uppercase' },
  suggestionWineName: { fontSize: 18, fontWeight: 'bold', color: '#0f172a', marginBottom: 6 },
  suggestionReason: { fontSize: 14, color: '#475569', lineHeight: 22, marginBottom: 12 },
  openWineBtn: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', backgroundColor: '#e0e7ff', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 10 },
  openWineBtnText: { color: '#4f46e5', fontWeight: 'bold', fontSize: 14, marginRight: 4 },
  recalcSecondaryBtn: { marginTop: 8, paddingVertical: 12, paddingHorizontal: 16, alignItems: 'center', backgroundColor: 'transparent', borderWidth: 1, borderColor: '#4f46e5', borderRadius: 12, borderStyle: 'dashed' },
  recalcSecondaryBtnText: { color: '#4f46e5', fontWeight: 'bold', fontSize: 15, textAlign: 'center' },
  sectionHeaderWrap: { flexDirection: 'row', alignItems: 'center', marginBottom: 12, marginLeft: 4 },
  sectionLabel: { fontSize: 18, fontWeight: 'bold', color: '#0f172a', marginLeft: 8 },
  card: { backgroundColor: 'white', borderRadius: 20, padding: 20, marginBottom: 32, boxShadow: '0px 2px 8px rgba(0,0,0,0.05)', elevation: 2 },
  cardOutdated: { borderWidth: 2, borderColor: '#fed7aa' },
  outdatedBanner: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff7ed', padding: 12, borderRadius: 12, marginBottom: 20, flexWrap: 'wrap', gap: 6 },
  outdatedText: { flex: 1, minWidth: 200, color: '#c2410c', fontSize: 13, fontWeight: '600', marginLeft: 4, lineHeight: 18 },
  barContainer: { marginBottom: 16 },
  barLabelRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  barLabel: { fontSize: 14, fontWeight: 'bold', flex: 1, paddingRight: 10 }, 
  barValue: { fontSize: 14, textAlign: 'right' },
  barBackground: { height: 12, borderRadius: 6, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 6 },
  reportBox: { backgroundColor: '#f8fafc', padding: 16, borderRadius: 16, marginTop: 16, borderWidth: 1, borderColor: '#e2e8f0' },
  reportTitle: { fontSize: 15, fontWeight: 'bold', color: '#0f172a', marginBottom: 8 },
  reportText: { fontSize: 14, color: '#475569', lineHeight: 22 },
  recalcBtn: { backgroundColor: '#e11d48', paddingVertical: 14, paddingHorizontal: 16, borderRadius: 12, alignItems: 'center', marginTop: 20 },
  recalcBtnText: { color: 'white', fontWeight: 'bold', fontSize: 15, textAlign: 'center' },
  emptyAuditBox: { alignItems: 'center', paddingVertical: 20 },
  emptyAuditTitle: { fontSize: 18, fontWeight: 'bold', color: '#0f172a', marginBottom: 8 },
  emptyAuditText: { fontSize: 14, color: '#64748b', textAlign: 'center', lineHeight: 20, marginBottom: 20 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.7)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalBoxBase: { backgroundColor: 'white', borderRadius: 24, padding: 24, width: '100%', maxWidth: 400 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginLeft: 12 },
  modalMessage: { fontSize: 16, color: '#475569', lineHeight: 24, marginBottom: 24 },
  modalConfirmBtn: { backgroundColor: '#0f172a', paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  modalConfirmText: { color: 'white', fontWeight: 'bold', fontSize: 16 }
});